import { useQuery } from '@apollo/client';
import deepEqual from 'fast-deep-equal';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSessionStorage } from '~/modules/common/hooks';
import { useMeContext } from '~/modules/me';
import RESOURCE_REQUESTS_ALLOCATIONS_QUERY from '~/modules/resourcing/common/fragments/resourceRequestsQuery';
import {
  ResourceAllocationStatus,
  ResourceRequestStatus,
  ResourceMatchesStatus
} from '~/types';
import { updateProjectTotalsCacheForNewRequest } from '~/modules/resourcing/enhancers/updateResourceRequestTotalsCache';

const requestStatusList = [
  ResourceRequestStatus.Allocationrejected,
  ResourceRequestStatus.Draft,
  ResourceRequestStatus.Rejected,
  ResourceRequestStatus.Submitted,
  ResourceRequestStatus.Tentative,
  ResourceRequestStatus.Tobehired
];

export const tryLoadCachedProjectResourceRequestsQuery = ({
  proxy,
  variables
}) => {
  try {
    return proxy.readQuery({
      query: RESOURCE_REQUESTS_ALLOCATIONS_QUERY,
      variables
    });
  } catch (e) {
    return null;
  }
};

const getFilterInput = ({ searchText, roleUris, userIds, resourcePoolIds }) => {
  const hasFilter =
    Boolean(searchText) ||
    Boolean(roleUris) ||
    Boolean(userIds) ||
    Boolean(resourcePoolIds);

  return hasFilter
    ? {
        roles: roleUris,
        text: searchText,
        users: userIds,
        resourcePools: resourcePoolIds
      }
    : undefined;
};

export const updateCachedProjectResourceRequests = ({
  newRequest,
  variables,
  filter,
  isResourceActualModeEnabled,
  skipProjectTotalsCacheUpdate
}) => client => {
  const results = tryLoadCachedProjectResourceRequestsQuery({
    proxy: client.cache,
    variables
  });

  if (!skipProjectTotalsCacheUpdate) {
    updateProjectTotalsCacheForNewRequest({
      proxy: client.cache,
      newRequest,
      filter: filter || {},
      isResourceActualModeEnabled
    });
  }

  if (!results) return;

  const { resourceRequests } = results;

  client.writeQuery({
    query: RESOURCE_REQUESTS_ALLOCATIONS_QUERY,
    variables,
    data: {
      resourceRequests: {
        ...resourceRequests,
        resourceRequests: [
          ...resourceRequests.resourceRequests,
          {
            ...newRequest,
            resourceMatches: null,
            resourceMatchesStatus: ResourceMatchesStatus.InProgress
          }
        ]
      }
    }
  });
};

const useProjectResourceRequests = ({
  projectUri,
  roleUris,
  searchText,
  userIds,
  resourcePoolIds,
  sort
}) => {
  const {
    permissionsMap,
    featureFlags: { isPsaPraaResourceMatchingEnabled }
  } = useMeContext();

  const isResourceAssignmentEnabled = Boolean(
    permissionsMap[
      'urn:replicon:resource-assignment-action:edit-resource-assignments'
    ]
  );

  const filter = getFilterInput({
    roleUris,
    searchText,
    userIds,
    resourcePoolIds
  });

  // useScheduleDetailsForResourceUser fetches and merges holidays/timeoffs, so
  // this query doesn't need showTimeOff/showHolidays/chartDateRange.
  const variables = useMemo(
    () => ({
      cursor: null,
      limit: 50,
      projectUri,
      filter,
      sort,
      requestStatusList,
      minimumLimit: 10,
      isPsaPraaResourceMatchingEnabled
    }),
    [projectUri, filter, sort, isPsaPraaResourceMatchingEnabled]
  );

  const { storedValue, setValue } = useSessionStorage(
    'RESOURCE-REQUESTS-QUERY-VARIABLES',
    null
  );

  useEffect(() => {
    if (!deepEqual(storedValue, variables)) {
      setValue(variables);
    }
  }, [variables, setValue, storedValue]);

  const [loadingMore, setLoadingMore] = useState(false);
  const { loading, data, fetchMore } = useQuery(
    RESOURCE_REQUESTS_ALLOCATIONS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      variables,
      errorPolicy: 'all'
    }
  );

  const resourceRequests = data?.resourceRequests?.resourceRequests || [];
  const nextPageCursor = data?.resourceRequests?.nextPageCursor;
  const hasMoreRows = Boolean(nextPageCursor);

  const loadMoreRows = useCallback(async () => {
    if (loading || loadingMore || !hasMoreRows) return;

    setLoadingMore(true);
    try {
      await fetchMore({
        query: RESOURCE_REQUESTS_ALLOCATIONS_QUERY,
        variables: {
          ...variables,
          cursor: nextPageCursor
        }
      });
    } finally {
      setLoadingMore(false);
    }
  }, [loading, loadingMore, hasMoreRows, fetchMore, variables, nextPageCursor]);

  return {
    hasMoreRows,
    loadingRows: loading,
    loadingMoreRows: loadingMore,
    loadMoreRows,
    resourceRequests: (resourceRequests || [])
      .filter(req => requestStatusList.includes(req.requestStatus))
      .map(req => ({
        ...req,
        resourceAllocations: req.resourceAllocations.filter(
          alloc =>
            isResourceAssignmentEnabled ||
            alloc.allocationStatus !== ResourceAllocationStatus.Draft
        ),
        isDelaDraftResourceRequest:
          isPsaPraaResourceMatchingEnabled &&
          req.sourceMetadata?.sourcedFromAI &&
          req.requestStatus === ResourceRequestStatus.Draft
      }))
  };
};

export default useProjectResourceRequests;
